import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useStyles } from './TreatmentPlanRecommendations.styles';
import { btnSize, btnType, Button } from '../../../../../../packages/ui/atoms/button';

// Group recommendations into a hierarchy
const groupRecommendations = recommendations => {
  // Create maps to track different entity types
  const problemMap = new Map();
  const goalMap = new Map();
  const objectiveMap = new Map();
  // Track which goals are nested under a new problem
  const nestedGoalIds = new Set();
  // Track which goals are standalone (not nested under a new problem)
  const standaloneGoalIds = new Set();
  // Group updates/adds to existing entities by composite key: entityType:entityReference
  const updatesByCompositeKey = {};

  // First pass: map all entities by their generatedId
  recommendations.forEach(rec => {
    if (rec.recommendationType.includes('ADD')) {
      if (rec.entityType === 'PROBLEM') {
        problemMap.set(rec.generatedId, {
          recommendation: rec,
          goals: [],
          type: 'NEW',
        });
      } else if (rec.entityType === 'GOAL') {
        goalMap.set(rec.generatedId, {
          recommendation: rec,
          objectives: [],
          type: 'NEW',
        });
      } else if (rec.entityType === 'OBJECTIVE') {
        objectiveMap.set(rec.generatedId, {
          recommendation: rec,
          type: 'NEW',
        });
      }
    } else {
      // This is an update to an existing entity
      const compositeKey = `${rec.entityType}:${rec.entityReference}`;
      if (!updatesByCompositeKey[compositeKey]) {
        updatesByCompositeKey[compositeKey] = [];
      }
      updatesByCompositeKey[compositeKey].push(rec);
    }
  });

  // Second pass: establish parent-child relationships for ADDs
  // Link goals to their parent problems
  for (const [goalId, goalData] of goalMap.entries()) {
    const parentRef = goalData.recommendation.entityReference;
    // Parent can be a new problem (generatedId) or an existing problem (id)
    const parentProblem = problemMap.get(parentRef);
    if (parentProblem) {
      parentProblem.goals.push(goalData);
      nestedGoalIds.add(goalId);
    } else {
      // This is a goal to be added to an existing problem, should be its own group
      // But to avoid duplicates, just mark as standalone and do not add to updateGroups
      standaloneGoalIds.add(goalId);
    }
  }

  // Link objectives to their parent goals
  for (const [objectiveId, objectiveData] of objectiveMap.entries()) {
    const parentRef = objectiveData.recommendation.entityReference;
    // Parent can be a new goal (generatedId) or an existing goal (id)
    const parentGoal = goalMap.get(parentRef);
    if (parentGoal) {
      parentGoal.objectives.push(objectiveData);
    } else {
      // This is an objective to be added to an existing goal, should be its own group
      const compositeKey = `OBJECTIVE:${objectiveId}`;
      if (!updatesByCompositeKey[compositeKey]) {
        updatesByCompositeKey[compositeKey] = [];
      }
      updatesByCompositeKey[compositeKey].push(objectiveData.recommendation);
    }
  }

  // Collect standalone new goals (not nested under a new problem)
  const standaloneNewGoals = Array.from(goalMap.entries())
    .filter(([goalId]) => !nestedGoalIds.has(goalId))
    .map(([, goalData]) => goalData);

  // Final result structure
  // newEntities: array of new problems (with nested new goals/objectives) + standalone new goals
  // updateGroups: map of compositeKey -> updates/adds to existing entities
  return {
    newEntities: [...Array.from(problemMap.values()), ...standaloneNewGoals],
    updateGroups: updatesByCompositeKey,
  };
};

const OurRecommendations = ({
  recommendations = [],
  hasRecommendations,
  onApplyRecommendation,
  onApplyAllRecommendations,
}) => {
  const classes = useStyles();

  // Group recommendations
  const groupedRecommendations = useMemo(() => {
    if (!recommendations || recommendations.length === 0) return { newEntities: [], updateGroups: {} };
    return groupRecommendations(recommendations);
  }, [recommendations]);

  const renderRecommendations = () => {
    const { newEntities, updateGroups } = groupedRecommendations;

    // Flatten recommendations for rendering
    const allRecGroups = [
      ...newEntities.map(problemData => ({
        type: 'new',
        data: problemData,
      })),
      ...Object.entries(updateGroups).map(([entityRef, updates]) => ({
        type: 'update',
        entityRef,
        data: updates,
      })),
    ];

    return (
      <>
        {allRecGroups.map((group, index) => {
          if (group.type === 'new') {
            return renderNewEntityGroup(group.data, index);
          } else {
            return renderUpdateGroup(group.entityRef, group.data, index);
          }
        })}
      </>
    );
  };

  const renderNewEntityGroup = (entityData, index) => {
    // If this is a problem (has goals)
    if (entityData.goals) {
      return (
        <Box
          key={entityData.recommendation.generatedId || `problem-${entityData.recommendation.id || index}`}
          className={classes.recommendationCard}
        >
          <Box display="flex" flexDirection="column" gap="4px">
            <Box className={classes.cardTitle}>Add Problem</Box>
            <Box className={classes.cardDescription}>{entityData.recommendation.recommendedValue}</Box>

            {/* Nested goals if any */}
            {entityData.goals.length > 0 && (
              <Box paddingLeft="12px" marginTop="4px">
                {entityData.goals.map((goalData, idx) => (
                  <Box
                    key={goalData.recommendation.generatedId || `goal-${goalData.recommendation.id || idx}`}
                  >
                    <Box marginBottom="4px">
                      <Box className={classes.cardSubTitle}>• Add Goal</Box>
                      <span className={classes.cardDescription}>
                        {goalData.recommendation.recommendedValue}
                      </span>
                    </Box>

                    {/* Nested objectives if any */}
                    {goalData.objectives.length > 0 && (
                      <Box paddingLeft="12px" marginTop="4px">
                        {goalData.objectives.map((objData, oidx) => (
                          <Box
                            key={
                              objData.recommendation.generatedId ||
                              `objective-${objData.recommendation.id || oidx}`
                            }
                            marginBottom="4px"
                          >
                            <Box className={classes.cardSubTitle}>◦ Add Objective</Box>
                            <span className={classes.cardDescription}>
                              {objData.recommendation.recommendedValue}
                            </span>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Button
            size={btnSize.SMALL}
            className={classes.cardAction}
            variant={btnType.TEXT}
            onClick={() => {
              // Collect all recommendations in this tree
              const allRecs = [
                entityData.recommendation,
                ...entityData.goals.map(g => g.recommendation),
                ...entityData.goals.flatMap(g => g.objectives.map(o => o.recommendation)),
              ];
              onApplyRecommendation(allRecs);
            }}
          >
            Use recommendation
          </Button>
        </Box>
      );
    }
    // If this is a standalone goal (has objectives)
    if (entityData.objectives) {
      return (
        <Box
          key={entityData.recommendation.generatedId || `goal-${entityData.recommendation.id || index}`}
          className={classes.recommendationCard}
        >
          <Box display="flex" flexDirection="column" gap="4px">
            <Box className={classes.cardTitle}>Add Goal</Box>
            <Box className={classes.cardDescription}>{entityData.recommendation.recommendedValue}</Box>

            {/* Nested objectives if any */}
            {entityData.objectives.length > 0 && (
              <Box paddingLeft="12px" marginTop="4px">
                {entityData.objectives.map((objData, oidx) => (
                  <Box
                    key={
                      objData.recommendation.generatedId || `objective-${objData.recommendation.id || oidx}`
                    }
                    marginBottom="4px"
                  >
                    <Box className={classes.cardSubTitle}>• Add Objective</Box>
                    <span className={classes.cardDescription}>{objData.recommendation.recommendedValue}</span>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Button
            size={btnSize.SMALL}
            className={classes.cardAction}
            variant={btnType.TEXT}
            onClick={() => {
              // Collect all recommendations in this tree
              const allRecs = [
                entityData.recommendation,
                ...entityData.objectives.map(o => o.recommendation),
              ];
              onApplyRecommendation(allRecs);
            }}
          >
            Use recommendation
          </Button>
        </Box>
      );
    }
    // fallback
    return null;
  };

  const renderUpdateGroup = (entityRef, updates, index) => {
    // Determine the type of entity from the first update or infer it
    let entityType = updates[0].entityType;

    // Group updates by their type and entity type
    const grouped = {
      add: updates.filter(u => u.recommendationType.startsWith('ADD')),
      update: updates.filter(u => u.recommendationType.startsWith('UPDATE')),
    };

    return (
      <Box key={entityRef || `update-${index}`} className={classes.recommendationCard}>
        <Box display="flex" flexDirection="column" gap="4px">
          <Box className={classes.cardTitle}>
            {entityType === 'PROBLEM' ? 'Problem' : entityType === 'OBJECTIVE' ? 'Objective' : 'Goal'}
          </Box>

          {/* Show updates first */}
          {grouped.update.map(update => (
            <Box key={update.id} marginBottom="4px">
              <Box className={classes.cardSubTitle}>• Update</Box>
              <span className={classes.cardDescription}>{update.recommendedValue}</span>
            </Box>
          ))}

          {/* Show adds second */}
          {grouped.add.map(update => (
            <Box key={update.id} marginBottom="4px">
              <Box className={classes.cardSubTitle}>• Add</Box>
              <span className={classes.cardDescription}>{update.recommendedValue}</span>
            </Box>
          ))}
        </Box>

        <Button
          size={btnSize.SMALL}
          className={classes.cardAction}
          variant={btnType.TEXT}
          onClick={() => onApplyRecommendation(updates)}
        >
          Use recommendation
        </Button>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {hasRecommendations && renderRecommendations()}
    </Box>
  );
};
export { OurRecommendations as TreatmentPlanRecommendations };
